/**
 * «Отделы» — Spec 035 v0.4: the company of agents, one tree of units, in place of «Агенты»
 * (Kostya, 12.10: agents are staff). Top units first — people talk to their heads; each unit
 * opens level by level; then «Без отдела», the cards still to be placed, which are not launched
 * until then. On a computer a unit opens on the right. Step 5: what waits for people comes first —
 * the decisions the heads of top units asked. Spec 039 step 3: tabs in place of the link cards —
 * the company, its document, its tools, its PCs; the header says who works and the month's
 * spending; each unit says how many decisions wait for people. Every tab opens in place
 * (`?tab=`), so the header and the tabs never go away. Spec 048 step 2: «Радар» is a unit like
 * any other; its findings live in «Знания» → «Находки», and the old tab's link leads there.
 */

import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";

import { Departments, UnitBudgets, type UnplacedAgentOut } from "@/api/units";
import { NewAgentSheet } from "@/components/AgentForm";
import { AccountsBlock } from "@/components/StaffAccounts";
import { useCompanyView } from "@/components/CompanyStaff";
import { DevicesBlock } from "@/components/Devices";
import { OpenDecisions } from "@/components/UnitDecisions";
import { PlaceAgentSheet, UnitSheet } from "@/components/UnitForm";
import { Button } from "@/components/ui/Button";
import { SkeletonList } from "@/components/ui/Skeleton";
import { BlockTabs, Caption, EmptyNote, ListPane, Pill, ScreenHeader, useIsDesktop } from "@/kit";
import { cn } from "@/lib/cn";
import { monthSpent, shortTokens, workingCount } from "@/lib/company";
import { budgetLine, buildTree, headLine, helpersLine, KIND_LABEL, RUN_LABEL, runTone, staffLine, type TreeNode } from "@/lib/units";
import { CompanyBriefBlock } from "@/screens/CompanyBriefScreen";
import { DepartmentScreen } from "@/screens/DepartmentScreen";
import { ToolsBlock } from "@/screens/ToolsScreen";

type Tab = "company" | "brief" | "tools" | "accounts" | "pcs";
const TABS: Tab[] = ["company", "brief", "tools", "accounts", "pcs"];

export function DepartmentsScreen() {
  const desktop = useIsDesktop();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const archived = params.get("archived") === "1";
  const asked = params.get("tab") as Tab | null;
  const tab: Tab = !archived && asked && TABS.includes(asked) ? asked : "company";
  const list = useQuery({ queryKey: ["departments", "list", archived], queryFn: () => Departments.list(archived) });
  const unplaced = useQuery({ queryKey: ["departments", "unplaced"], queryFn: Departments.unplaced, enabled: !archived });
  const budgets = useQuery({ queryKey: ["departments", "budgets"], queryFn: UnitBudgets.all, enabled: !archived });
  const company = useCompanyView();
  const budgetOf = (id: string) => budgets.data?.find((b) => b.unit_id === id);
  const decisionsOf = (id: string) => company.data?.units.find((u) => u.id === id)?.open_decisions ?? 0;
  const [creating, setCreating] = useState(false);
  const [newAgent, setNewAgent] = useState(false);
  const [placing, setPlacing] = useState<UnplacedAgentOut | null>(null);
  const [closed, setClosed] = useState<Set<string>>(new Set());
  // A link to the old «Радар» tab opens its findings in «Знания».
  if (params.get("tab") === "radar") return <Navigate to="/knowledge/radar" replace />;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const open = params.get("open");
  const openUnit = (id: string) => (desktop ? setParam("open", id) : navigate(`/departments/${id}`));
  const units = list.data ?? [];
  const tree = buildTree(units);
  const free = unplaced.data ?? [];

  const toggle = (id: string) =>
    setClosed((now) => {
      const next = new Set(now);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onTab = (key: Tab) => setParam("tab", key === "company" ? null : key);

  const row = (node: TreeNode, level: number) => {
    const u = node.unit;
    const shut = closed.has(u.id);
    const decisions = decisionsOf(u.id);
    return (
      <li key={u.id}>
        <div className={cn("flex items-start border-b border-p-border", open === u.id && "bg-p-surface-2")}>
          <span style={{ width: 12 + level * 20 }} className="shrink-0" aria-hidden="true" />
          {node.children.length > 0 ? (
            <button type="button" aria-label={shut ? "Раскрыть" : "Свернуть"} onClick={() => toggle(u.id)}
              className="flex min-h-11 w-8 shrink-0 items-center justify-center text-p-text-muted">
              {shut ? <ChevronRight className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
            </button>
          ) : (
            <span className="w-8 shrink-0" aria-hidden="true" />
          )}
          <button type="button" onClick={() => openUnit(u.id)} className="flex min-w-0 flex-1 flex-col gap-1 py-3 pr-4 text-left press-surface">
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[15px] font-semibold leading-5 text-p-text">{u.name}</span>
              <span className="text-meta text-p-text-muted">{KIND_LABEL[u.kind]}</span>
            </span>
            {/* The head's state is the pill below: the line names who heads it, once. */}
            <span className={cn("text-meta", u.head ? "text-p-text" : "text-p-needs")}>
              {u.head ? `Глава: ${u.head.name}` : headLine(u)}
            </span>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-meta text-p-text-muted">
              <span>{[staffLine(u.members.length), helpersLine(u.helpers ?? 0)].filter(Boolean).join(" · ")}</span>
              {decisions > 0 && <Pill tone="needs">{`ждёт решения: ${decisions}`}</Pill>}
              {u.running > 0 && <Pill tone="done">{`работает: ${u.running}`}</Pill>}
              {u.open_orders > 0 && <Pill tone="review">{`поручений: ${u.open_orders}`}</Pill>}
              {u.members.some((m) => m.waits_launch) && <Pill tone="needs">ждёт запуска</Pill>}
              {budgetOf(u.id)?.tokens_per_month != null && (
                <Pill tone={budgetOf(u.id)!.over ? "blocked" : "waiting"}>{budgetLine(budgetOf(u.id)!)}</Pill>
              )}
              {u.archived_at && <span>в архиве</span>}
            </span>
          </button>
        </div>
        {!shut && node.children.length > 0 && <ul>{node.children.map((c) => row(c, level + 1))}</ul>}
      </li>
    );
  };

  const sub = company.data
    ? `Работают: ${workingCount(company.data)} · ${shortTokens(monthSpent(company.data))} токенов за месяц`
    : "Агенты — сотрудники подразделений. Вы говорите с руководителями верхних.";

  const companyTab = (
    <>
      {!archived && <OpenDecisions />}
      {list.isLoading && <SkeletonList count={3} />}
      {list.isError && (
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void list.refetch()}>Повторить</Button>}>
          Не удалось загрузить подразделения.
        </EmptyNote>
      )}
      {list.data && units.length === 0 && (
        <EmptyNote action={!archived && <Button size="compact" variant="secondary" onClick={() => setCreating(true)}>Завести подразделение</Button>}>
          {archived ? "В архиве пусто." : "Подразделений пока нет. Заведи верхнее — например, департамент маркетинга — и поставь в него сотрудников."}
        </EmptyNote>
      )}
      {tree.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface [&>li:last-child>div]:border-0">
          {tree.map((node) => row(node, 0))}
        </ul>
      )}

      {!archived && (
        <section className="flex flex-col gap-2" aria-label="Без отдела">
          <div className="flex items-center justify-between gap-2">
            <Caption>Без отдела</Caption>
            <Button size="compact" variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setNewAgent(true)}>
              Сотрудник
            </Button>
          </div>
          {free.length === 0 ? (
            <p className="text-meta text-p-text-muted">Все сотрудники в подразделениях.</p>
          ) : (
            <>
              <p className="text-meta text-p-text-muted">Эти сотрудники не запускаются, пока их не поставят в подразделение. Помощники приходят в отдел вместе со своим сотрудником.</p>
              <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
                {free.map((a) => (
                  <li key={a.agent_id} className="flex items-center gap-3 border-b border-p-border px-4 py-3 last:border-0">
                    <button type="button" className="flex min-w-0 flex-1 flex-col gap-1 text-left press-surface"
                      onClick={() => navigate(`/agents/${a.agent_id}`)}>
                      <span className="text-[15px] font-semibold leading-5 text-p-text">{a.name}</span>
                      {a.purpose_md && <span className="line-clamp-2 text-meta text-p-text-muted">{a.purpose_md}</span>}
                      <span className="text-meta text-p-text-muted">
                        <Pill tone={runTone(a.run_state) === "done" ? "done" : "waiting"}>{RUN_LABEL[a.run_state]}</Pill>
                      </span>
                    </button>
                    <Button size="compact" variant="secondary" onClick={() => setPlacing(a)}>Поставить</Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-link" onClick={() => setParam("archived", archived ? null : "1")}>
        {archived ? "Все подразделения" : "Архив"}
      </button>
    </>
  );

  const body = (
    <div className="flex flex-col gap-3">
      <ScreenHeader
        title="Отделы"
        sub={sub}
        trailing={
          <Button size="compact" variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setCreating(true)}>
            Подразделение
          </Button>
        }
      />
      {!archived && (
        <BlockTabs<Tab>
          id="departments"
          label="Разделы отделов"
          value={tab}
          onChange={onTab}
          tabs={[
            { key: "company", label: "Компания", alert: (company.data?.units ?? []).some((u) => u.open_decisions > 0) },
            { key: "brief", label: "О компании" },
            { key: "tools", label: "Инструменты" },
            // Spec 049: what staff members' emulators sign in with.
            { key: "accounts", label: "Аккаунты" },
            { key: "pcs", label: "Компьютеры", count: company.data?.pcs.filter((p) => p.online).length },
          ]}
        />
      )}
      {tab === "pcs" ? <DevicesBlock /> : tab === "brief" ? <CompanyBriefBlock /> : tab === "tools" ? <ToolsBlock /> : tab === "accounts" ? <AccountsBlock /> : companyTab}
      {creating && (
        <UnitSheet
          open
          onClose={() => setCreating(false)}
          onSaved={(saved) => {
            setCreating(false);
            openUnit(saved.id);
          }}
        />
      )}
      <PlaceAgentSheet key={placing?.agent_id ?? "none"} agent={placing} onClose={() => setPlacing(null)} />
      <NewAgentSheet open={newAgent} onClose={() => setNewAgent(false)}
        onCreated={(a) => { setNewAgent(false); navigate(`/agents/${a.id}`); }} />
    </div>
  );

  // Only «Компания» is a list of units: the other tabs take the whole width, with nothing beside.
  if (!desktop || tab !== "company") return body;
  return (
    <ListPane
      list={body}
      listWidth={400}
      open={Boolean(open)}
      onClose={() => setParam("open", null)}
      pane={open ? <DepartmentScreen key={open} departmentId={open} embedded onOpen={openUnit} /> : null}
      placeholder={units.length ? <EmptyNote>Выбери подразделение слева — оно откроется здесь.</EmptyNote> : null}
    />
  );
}
